import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useBrand } from "@/contexts/BrandContext";
import { ArrowRight } from "lucide-react";
import { clearNeedsWorkspacePick, markNeedsWorkspacePick, needsWorkspacePick } from "@/lib/workspacePick";
import { consentReturnPath } from "@/lib/oauth-consent";
import { APP_HOME_PATH } from "@/lib/copilot-home";

export default function Login() {
  const { session, loading } = useAuth();
  const { toast } = useToast();
  const brand = useBrand();
  const isEtb = brand.id === "etb";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  if (loading) return null;
  if (session) {
    const consentNext = consentReturnPath(searchParams.get("next"));
    return <Navigate to={consentNext ?? (needsWorkspacePick() ? "/select-workspace" : APP_HOME_PATH)} replace />;
  }

  // Exponential backoff with jitter for rate-limited auth (429)
  const loginWithBackoff = async (email: string, password: string, maxRetries = 3): Promise<{ data: any; error: any }> => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const result = await supabase.auth.signInWithPassword({ email, password });
      
      // If not rate limited, return immediately
      if (!result.error || !result.error.message?.includes("429") && result.error.status !== 429) {
        return result;
      }

      if (attempt < maxRetries) {
        // Exponential backoff: 2s, 4s, 8s + jitter (0-1s)
        const delay = Math.pow(2, attempt + 1) * 1000 + Math.random() * 1000;
        toast({ title: "Demasiados intentos", description: `Reintentando en ${Math.ceil(delay / 1000)}s...` });
        await new Promise(r => setTimeout(r, delay));
      }
    }
    return { data: null, error: { message: "Demasiados intentos de inicio de sesión. Espera unos segundos e intenta de nuevo." } };
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { data, error } = await loginWithBackoff(email, password);
    if (error) {
      toast({ title: "Error al iniciar sesión", description: error.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }
    
    // Brand isolation: verify user has at least one org matching current brand
    const userId = data?.user?.id;
    if (userId) {
      const { data: agents } = await supabase
        .from("agents")
        .select("organization_id")
        .eq("user_id", userId);
      
      if (agents && agents.length > 0) {
        const orgIds = [...new Set(agents.map(a => a.organization_id))];
        const { data: orgs } = await supabase
          .from("organizations")
          .select("id, brand")
          .in("id", orgIds);
        
        const brandOrgIds = new Set((orgs || []).filter(o => o.brand === brand.id).map(o => o.id));
        if (brandOrgIds.size === 0) {
          await supabase.auth.signOut();
          toast({ 
            title: "Acceso no autorizado", 
            description: `Tu cuenta no tiene acceso a ${brand.name}. Verifica que estás en la plataforma correcta.`, 
            variant: "destructive" 
          });
          setSubmitting(false);
          return;
        }

        // DE-653: post-login selector when the agent belongs to ≥2 workspaces of this brand.
        const multiWs = new Set(
          agents.filter((a) => brandOrgIds.has(a.organization_id)).map((a) => a.organization_id),
        );
        if (multiWs.size >= 2) markNeedsWorkspacePick();
        else clearNeedsWorkspacePick();
      } else {
        clearNeedsWorkspacePick();
      }
    }

    if (data?.user?.user_metadata?.must_change_password === true) {
      window.location.href = "/change-password";
      return;
    }
    setSubmitting(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4" style={{ backgroundColor: brand.palette.deepBg }}>
      {/* Mesh gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[120px] animate-pulse" style={{ backgroundColor: `${brand.palette.primary}33` }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] animate-pulse" style={{ backgroundColor: `${brand.palette.neon}1a`, animationDelay: "1s" }} />
        <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] rounded-full blur-[100px] animate-pulse" style={{ backgroundColor: `${brand.palette.deepBlue}26`, animationDelay: "2s" }} />
      </div>

      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center mb-8">
          <Link to="/" className={isEtb ? "px-4 py-2" : undefined}>
            <img
              src={isEtb ? brand.logos.navbarDark : brand.logos.login}
              alt={brand.shortName}
              className={`w-auto object-contain ${isEtb ? "h-24" : "h-16"}`}
            />
          </Link>
          <span className={`text-white/60 mt-2 font-medium ${isEtb ? "text-lg" : "text-sm"}`}>{brand.shortName}</span>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl" style={{ boxShadow: `0 25px 50px -12px ${brand.palette.primary}0d` }}>
          <div className="text-center mb-6">
            <h1 className="font-display text-2xl font-bold text-white mb-1">
              {isEtb ? "Haz crecer tu negocio, con herramientas de I.A." : "Iniciar sesión"}
            </h1>
            <p className="text-sm text-white/40">Ingresa a tu cuenta para continuar</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/60 text-xs">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20 focus-visible:ring-primary/50 focus-visible:border-primary/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/60 text-xs">Contraseña</Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-white/40 hover:text-white/60 transition-colors">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="h-11 w-full text-sm font-medium"
             
            >
              {submitting ? "Iniciando..." : "Iniciar sesión"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>

          {brand.id === "flow" && (
            <p className="text-center text-sm text-white/30 mt-6">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-primary hover:opacity-80 transition-colors font-medium">
                Crear cuenta
              </Link>
            </p>
          )}
        </div>

        {/* Back to home */}
        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-white/20 hover:text-white/40 transition-colors">
            ← Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
}
